/** Platform jobs facade (`/jobs`). */

import { CatalogJob } from '../../schemas/platform';
import { BackendClient, CatalogQuery, QueryEnvelope } from './BackendClient';

const DEFAULT_JOB_EXPAND = ['data', 'files', 'social', 'billing'];

export type JobVisibility = 'user' | 'public' | 'user_visible';

export interface JobListOptions extends CatalogQuery {
  /** Convenience filters (mapped to ``filter`` clauses). */
  service_id?: string | null;
  project_id?: string | null;
  endpoint?: string | null;
  job_status?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  job_id?: string | null;
  /** Legacy filter_mode → visibility mapping from the frontend creations service. */
  filter_mode?: 'user_only' | 'public_only' | 'public_and_user' | 'all';
}

const FILTER_MODE_TO_VISIBILITY: Record<string, JobVisibility> = {
  user_only: 'user',
  public_only: 'public',
  public_and_user: 'user_visible',
  all: 'user_visible',
};

export class JobsClient extends BackendClient {
  constructor(apiKey?: string, webBackendUrl?: string) {
    super(apiKey, webBackendUrl);
    this.addEndpoint('jobs', 'GET', 'optional');
    this.addEndpoint('jobs/webhooks/finished', 'POST', 'none');
  }

  private listFilters(options: JobListOptions): string[] {
    const filters = [...(options.filters || [])];
    if (options.service_id) filters.push(`service_id:eq:${options.service_id}`);
    if (options.project_id) filters.push(`project_id:eq:${options.project_id}`);
    if (options.endpoint) filters.push(`endpoint:eq:${options.endpoint}`);
    if (options.job_status != null) filters.push(`status:eq:${options.job_status}`);
    if (options.start_date) filters.push(`created_at:gte:${options.start_date}`);
    if (options.end_date) filters.push(`created_at:lte:${options.end_date}`);
    if (options.job_id) filters.push(`id:eq:${options.job_id}`);
    return filters;
  }

  async queryJobs(query: JobListOptions = {}): Promise<QueryEnvelope<CatalogJob>> {
    const visibility =
      query.visibility ??
      (query.filter_mode ? FILTER_MODE_TO_VISIBILITY[query.filter_mode] : undefined) ??
      'user_visible';
    const raw = await this.getJson(
      'jobs',
      this.catalogQuery({
        ...query,
        filters: this.listFilters(query),
        expand: query.expand ?? DEFAULT_JOB_EXPAND,
        sort: query.sort ?? 'created_at:desc',
        visibility,
      }),
      // Non-public listings require a token; optional lets public work anonymously.
      visibility === 'public' ? 'optional' : 'required',
    );
    const envelope = this.asEnvelope<Record<string, any>>(raw);
    return {
      ...envelope,
      entities: envelope.entities.map((row) => new CatalogJob(row)),
    };
  }

  async getJob(
    jobId: string,
    query: Pick<CatalogQuery, 'expand' | 'fields'> = {},
  ): Promise<CatalogJob | null> {
    const route = `jobs/${encodeURIComponent(jobId)}`;
    this.addEndpoint(route, 'GET', 'optional');
    const raw = await this.getJson(route, this.catalogQuery({
      expand: query.expand ?? DEFAULT_JOB_EXPAND,
      fields: query.fields,
    }));
    return raw ? new CatalogJob(raw) : null;
  }

  async refreshJob(jobId: string): Promise<Record<string, any>> {
    return this.postJson('jobs/webhooks/finished', undefined, { job_id: jobId }, 'none');
  }

  async updateJob(
    jobId: string,
    options: { display_name?: string; is_public?: boolean } = {},
  ): Promise<boolean> {
    const route = `jobs/${encodeURIComponent(jobId)}`;
    this.addEndpoint(route, 'PATCH', 'required');
    const result = await this.patchJson(route, undefined, {
      display_name: options.display_name,
      is_public: options.is_public,
    });
    return result === true || result == null;
  }

  async updateSocialMetrics(
    jobId: string,
    options: {
      increase_views?: boolean;
      increase_downloads?: boolean;
      increase_shares?: boolean;
      increase_likes?: boolean;
    } = {},
  ): Promise<boolean> {
    const route = `jobs/${encodeURIComponent(jobId)}/social`;
    this.addEndpoint(route, 'POST', 'optional');
    const result = await this.postJson(route, undefined, options);
    return result === true || result == null;
  }

  async deleteJob(jobId: string): Promise<boolean> {
    const route = `jobs/${encodeURIComponent(jobId)}`;
    this.addEndpoint(route, 'DELETE', 'required');
    const result = await this.deleteJson(route);
    return result === true || result == null;
  }
}
